import type { HelloAck, SignalEnvelope } from "@wakeloop/protocol";
import { catchupResponseSchema } from "@wakeloop/schemas";
import {
  getAuthSessionState,
  type StoredAuthSession,
} from "../config/auth-session";
import { buildWakeLoopProfileIdentityRequestHeadersWithAuth } from "../config/request-headers";
import { toSpaceSignalsUrl } from "../protocol/urls";
import { parseIncoming } from "../protocol/wire";
import { fetchSpaceRequestWithTimeout } from "./fetch-with-timeout";
import type { SpaceSessionProfileIdentity } from "./session-profile";

const CATCHUP_PAGE_LIMIT = 200;
const MAX_CATCHUP_PAGES = 200;
const RECENT_HISTORY_DEFAULT_LIMIT = 100;
const RECENT_HISTORY_MAX_LIMIT = 500;
const CATCHUP_SEEN_SIGNAL_IDS_MAX = 50_000;
const LAST_SEEN_FUTURE_SKEW_TOLERANCE_MS = 30_000;

type ReplaySignalsInput = {
  seenSignalIds: Set<string>;
  onEvent: (event: SignalEnvelope | HelloAck) => void;
};

type ShouldIgnoreIncomingSignalReason =
  | "seen_replay_duplicate"
  | "older_than_live_floor"
  | null;

type ShouldIgnoreIncomingSignalResult = {
  ignore: boolean;
  reason: ShouldIgnoreIncomingSignalReason;
};

export function rememberSeenSignalId(
  seenSignalIds: Set<string>,
  signalId: string
): void {
  if (seenSignalIds.has(signalId)) {
    return;
  }

  seenSignalIds.add(signalId);
  while (seenSignalIds.size > CATCHUP_SEEN_SIGNAL_IDS_MAX) {
    const oldestSignalId = seenSignalIds.values().next().value;
    if (typeof oldestSignalId !== "string") {
      return;
    }
    seenSignalIds.delete(oldestSignalId);
  }
}

function emitReplaySignals(
  input: ReplaySignalsInput,
  signals: readonly unknown[]
): void {
  for (const item of signals) {
    const event = parseIncoming(item);
    if (!event || isServerErrorFrameMessage(event)) {
      continue;
    }
    if (!isSignalEnvelope(event)) {
      input.onEvent(event);
      continue;
    }
    if (input.seenSignalIds.has(event.id)) {
      continue;
    }
    input.onEvent(event);
    rememberSeenSignalId(input.seenSignalIds, event.id);
  }
}

function isSignalEnvelope(
  event: SignalEnvelope | HelloAck
): event is SignalEnvelope {
  return "id" in event && typeof event.id === "string";
}

function isServerErrorFrameMessage(
  event: SignalEnvelope | HelloAck | { error: string }
): event is { error: string; requestId?: string; clientMessageId?: string } {
  return typeof (event as { error?: unknown }).error === "string";
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

async function fetchCatchupPage(input: {
  authSession: StoredAuthSession | null;
  profileIdentity: SpaceSessionProfileIdentity;
  baseUrl: string;
  space: string;
  password?: string;
  afterTs: number;
  cursor: string | null;
}): Promise<ReturnType<typeof parseCatchupResponse>> {
  const url = new URL(toSpaceSignalsUrl(input.baseUrl, input.space));
  url.searchParams.set("afterTs", String(input.afterTs));
  url.searchParams.set("limit", String(CATCHUP_PAGE_LIMIT));
  if (input.cursor) {
    url.searchParams.set("cursor", input.cursor);
  }
  const endpoint = url.toString();

  const response = await fetchSpaceRequestWithTimeout({
    url: endpoint,
    operation: "space.catchup_page",
    init: {
      headers: await buildWakeLoopProfileIdentityRequestHeadersWithAuth({
        authSession: input.authSession,
        requestUrl: endpoint,
        profileIdentity: input.profileIdentity,
        spacePassword: input.password,
      }),
    },
  });
  const raw = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${raw}`);
  }

  return parseCatchupResponse(safeJsonParse(raw));
}

export async function replayMissedSignals(input: {
  profileIdentity: SpaceSessionProfileIdentity;
  baseUrl: string;
  space: string;
  password?: string;
  afterTs: number;
  seenSignalIds: Set<string>;
  onEvent: (event: SignalEnvelope | HelloAck) => void;
}): Promise<void> {
  const authSession = await resolveCachedReplayAuthSession();
  let cursor: string | null = null;
  const seenCursors = new Set<string>();
  let pagesFetched = 0;
  for (;;) {
    if (cursor && seenCursors.has(cursor)) {
      throw new Error("catch-up pagination cursor loop detected");
    }
    if (pagesFetched >= MAX_CATCHUP_PAGES) {
      throw new Error("catch-up exceeded max pages");
    }
    if (cursor) {
      seenCursors.add(cursor);
    }
    const page = await fetchCatchupPage({
      authSession,
      profileIdentity: input.profileIdentity,
      baseUrl: input.baseUrl,
      space: input.space,
      password: input.password,
      afterTs: input.afterTs,
      cursor,
    });
    pagesFetched += 1;
    emitReplaySignals(input, page.signals);

    if (!page.nextCursor || page.nextCursor === cursor) {
      return;
    }
    cursor = page.nextCursor;
  }
}

export async function replayRecentSignals(input: {
  profileIdentity: SpaceSessionProfileIdentity;
  baseUrl: string;
  space: string;
  password?: string;
  limit: number;
  seenSignalIds: Set<string>;
  onEvent: (event: SignalEnvelope | HelloAck) => void;
}): Promise<void> {
  if (input.limit <= 0) {
    return;
  }

  const url = new URL(toSpaceSignalsUrl(input.baseUrl, input.space));
  url.searchParams.set(
    "limit",
    String(Math.max(1, Math.min(input.limit, RECENT_HISTORY_MAX_LIMIT)))
  );
  url.searchParams.set("recent", "1");
  const endpoint = url.toString();
  const authSession = await resolveCachedReplayAuthSession();

  const response = await fetchSpaceRequestWithTimeout({
    url: endpoint,
    operation: "space.recent_history",
    init: {
      headers: await buildWakeLoopProfileIdentityRequestHeadersWithAuth({
        authSession,
        requestUrl: endpoint,
        profileIdentity: input.profileIdentity,
        spacePassword: input.password,
      }),
    },
  });
  const raw = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${raw}`);
  }

  const parsed = safeJsonParse(raw);
  const page = parseCatchupResponse(parsed);
  emitReplaySignals(input, page.signals);
}

async function resolveCachedReplayAuthSession(): Promise<StoredAuthSession | null> {
  const sessionState = await getAuthSessionState();
  if (sessionState.state !== "valid" || sessionState.session === null) {
    return null;
  }
  return sessionState.session;
}

export function normalizeRecentHistoryLimit(value: number | undefined): number {
  if (value === undefined) {
    return RECENT_HISTORY_DEFAULT_LIMIT;
  }
  if (!Number.isInteger(value) || value < 0) {
    throw new Error("history limit must be a non-negative integer");
  }
  return Math.min(value, RECENT_HISTORY_MAX_LIMIT);
}

export function resolveInitialLiveFloorTs(input: {
  lastSeenTs: number | null;
  nowMs: number;
}): number {
  if (
    typeof input.lastSeenTs !== "number" ||
    !Number.isFinite(input.lastSeenTs)
  ) {
    return 0;
  }

  const normalizedLastSeenTs = Math.max(0, Math.trunc(input.lastSeenTs));
  const maxAllowedFloorTs = input.nowMs + LAST_SEEN_FUTURE_SKEW_TOLERANCE_MS;

  if (normalizedLastSeenTs > maxAllowedFloorTs) {
    return input.nowMs;
  }

  return normalizedLastSeenTs;
}

export function parseCatchupResponse(value: unknown): {
  signals: unknown[];
  nextCursor: string | null;
} {
  const parsed = catchupResponseSchema.safeParse(value);
  if (!parsed.success) {
    throw new Error("Unexpected catch-up response");
  }

  return parsed.data;
}

export function shouldIgnoreIncomingSignal(input: {
  event: SignalEnvelope | HelloAck;
  liveFloorTs: number;
  seenSignalIds: Set<string>;
  selfProfileId?: string;
}): ShouldIgnoreIncomingSignalResult {
  const { event, liveFloorTs, seenSignalIds } = input;
  if (!isSignalEnvelope(event)) {
    return { ignore: false, reason: null };
  }

  if (seenSignalIds.has(event.id)) {
    return { ignore: true, reason: "seen_replay_duplicate" };
  }

  const selfProfileId = String(input.selfProfileId ?? "").trim();
  const senderProfileId = String(event.profile?.profileId ?? "").trim();
  if (selfProfileId && senderProfileId === selfProfileId) {
    return { ignore: false, reason: null };
  }

  if (event.ts < liveFloorTs) {
    return { ignore: true, reason: "older_than_live_floor" };
  }

  return { ignore: false, reason: null };
}

export function processIgnoredOlderThanLiveFloorSignal(input: {
  event: SignalEnvelope | HelloAck;
  seenSignalIds: Set<string>;
  onDispatchActivityEvent: (event: SignalEnvelope) => void;
}): boolean {
  if (!isSignalEnvelope(input.event)) {
    return false;
  }
  rememberSeenSignalId(input.seenSignalIds, input.event.id);
  input.onDispatchActivityEvent(input.event);
  return true;
}
